'use client';

import {
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import {
  IngestionSourceType,
  KafkaKeyHandling,
  KafkaMessageFormat,
  KafkaStartingOffset,
} from '@repo/api-gw-sdk';

import type { StepProps } from '@/components/wizard/StepProps';

import type { IngestionPipelineWizardState } from '../IngestionPipelineWizardState';

export function PipelineDetailsStep(
  props: StepProps<IngestionPipelineWizardState>
) {
  const { name, namespace, tableName, sourceType, existingPipelineNames } =
    props.wizardState;

  const isKafka =
    sourceType === IngestionSourceType.INGESTION_SOURCE_TYPE_KAFKA;

  const isNameTaken =
    name.trim() !== '' &&
    !props.wizardState.isUpdateMode &&
    existingPipelineNames.has(name.trim());

  const canGoNext =
    name.trim() !== '' &&
    !isNameTaken &&
    namespace.trim() !== '' &&
    tableName.trim() !== '';

  const handleSourceTypeChange = (newSourceType: IngestionSourceType) => {
    props.setWizardState((s) => ({
      ...s,
      sourceType: newSourceType,
      ...(newSourceType !== IngestionSourceType.INGESTION_SOURCE_TYPE_S3 && {
        s3Bucket: '',
        s3Region: '',
        s3OwnerAccountId: '',
      }),
      ...(newSourceType !== IngestionSourceType.INGESTION_SOURCE_TYPE_GCS && {
        gcsBucket: '',
        gcsProjectId: '',
        gcsLocation: '',
      }),
      ...(newSourceType !== IngestionSourceType.INGESTION_SOURCE_TYPE_KAFKA && {
        kafkaBootstrapServers: '',
        kafkaTopic: '',
        kafkaConsumerGroupId: '',
        kafkaStartingOffset: KafkaStartingOffset.KAFKA_STARTING_OFFSET_EARLIEST,
        kafkaMessageFormat: KafkaMessageFormat.KAFKA_MESSAGE_FORMAT_JSON,
        kafkaKeyHandling: KafkaKeyHandling.KAFKA_KEY_HANDLING_IGNORE,
      }),
    }));
  };

  const handleNext = () => {
    // Branch 0: S3/GCS path; Branch 1: Kafka path
    const branchIndex = isKafka ? 1 : 0;
    props.setNextStep(props.currentStep.next?.[branchIndex]);
  };

  return (
    <Stack sx={{ height: '100%' }} alignItems='center' justifyContent='center'>
      <Stack width='400px' gap='24px'>
        <FormControl fullWidth size='small'>
          <FormLabel>Pipeline Name</FormLabel>
          <TextField
            size='small'
            autoFocus
            placeholder='Enter pipeline name'
            value={name}
            error={isNameTaken}
            helperText={
              isNameTaken
                ? 'A pipeline with this name already exists'
                : undefined
            }
            onChange={(e) =>
              props.setWizardState((s) => ({ ...s, name: e.target.value }))
            }
            data-testid='pipeline-name-input'
          />
        </FormControl>

        <FormControl fullWidth size='small'>
          <FormLabel>Table Namespace</FormLabel>
          <TextField
            size='small'
            placeholder='e.g. analytics'
            value={namespace}
            onChange={(e) =>
              props.setWizardState((s) => ({
                ...s,
                namespace: e.target.value,
              }))
            }
            data-testid='pipeline-namespace-input'
          />
        </FormControl>

        <FormControl fullWidth size='small'>
          <FormLabel>Table Name</FormLabel>
          <TextField
            size='small'
            placeholder='e.g. clickstream_events'
            value={tableName}
            onChange={(e) =>
              props.setWizardState((s) => ({
                ...s,
                tableName: e.target.value,
              }))
            }
            data-testid='pipeline-table-name-input'
          />
        </FormControl>

        <FormControl fullWidth size='small'>
          <FormLabel>Source Type</FormLabel>
          <Select
            size='small'
            value={sourceType}
            onChange={(e) =>
              handleSourceTypeChange(e.target.value as IngestionSourceType)
            }
            data-testid='source-type-select'
          >
            <MenuItem value={IngestionSourceType.INGESTION_SOURCE_TYPE_S3}>
              S3
            </MenuItem>
            <MenuItem value={IngestionSourceType.INGESTION_SOURCE_TYPE_GCS}>
              GCS
            </MenuItem>
            <MenuItem value={IngestionSourceType.INGESTION_SOURCE_TYPE_KAFKA}>
              Kafka
            </MenuItem>
          </Select>
        </FormControl>

        <Button
          variant='contained'
          disabled={!canGoNext}
          onClick={handleNext}
          sx={{ height: '48px' }}
          data-testid='pipeline-details-continue-btn'
        >
          Continue
          <i className='material-symbols-arrow-right-alt ml-[8px]' />
        </Button>
      </Stack>
    </Stack>
  );
}
